import type { DdmStream, SourceDevice, Stream } from '@bloo-team/api-client'

/**
 * Pipeline-health predicates shared by Datafabric · Overview and the pages
 * its checks link to (docs/fabric-overview-requirements.md §The checks), so
 * a count on the overview and the filtered rows on the target page come
 * from the same rule.
 *
 * The backend carries no health flags: device `status` is only
 * active/inactive, `daily-stream-details` is a count so far today. Every
 * threshold below is a console-side judgement over those reads.
 */

/** An active device unseen for longer than this is stale. */
export const STALE_MS = 24 * 60 * 60 * 1000
/** A device unseen for longer than this is likely decommissioned, whatever its status. */
export const DECOMMISSIONED_MS = 30 * 24 * 60 * 60 * 1000
/**
 * Before this local hour every stream reads 0 "today", so silent streams are
 * muted. Browser-local until capture C2 says which clock starts "today".
 */
export const SILENT_MUTE_HOUR = 6

export type SourceState = 'active' | 'stale' | 'inactive' | 'decommissioned'

/** Last seen as epoch ms (the wire is float epoch seconds); a never-seen device falls back to creation. */
export function lastSeenMs(d: Pick<SourceDevice, 'last_seen_tstamp' | 'create_tstamp'>): number | null {
  const s = d.last_seen_tstamp ?? d.create_tstamp
  return s === null ? null : Math.round(s * 1000)
}

/**
 * One device's health. Decommissioned wins: past 30 days unseen, the status
 * no longer matters. With no timestamp at all nothing proves it gone, so
 * the status decides.
 */
export function sourceState(d: SourceDevice, now: number): SourceState {
  const seen = lastSeenMs(d)
  const age = seen === null ? null : now - seen
  if (age !== null && age > DECOMMISSIONED_MS) return 'decommissioned'
  if (d.status === 'inactive') return 'inactive'
  if (age !== null && age > STALE_MS) return 'stale'
  return 'active'
}

/**
 * Streams with no events so far today, by name. The daily read lists only
 * streams that received data, so a missing name counts as zero; the join is
 * on the exact name (the Streams page's `enrich` join).
 */
export function silentStreams(streams: Pick<Stream, 'stream'>[], daily: Record<string, number>): string[] {
  return streams
    .filter((s) => !((daily[s.stream] ?? 0) > 0))
    .map((s) => s.stream)
    .sort((a, b) => a.localeCompare(b))
}

/** True before `SILENT_MUTE_HOUR` local time, when "silent today" is not yet news. */
export function silentMuted(now: number): boolean {
  return new Date(now).getHours() < SILENT_MUTE_HOUR
}

/** Streams no device lists in its `stream[]`, by name. */
export function unmappedStreams(streams: Pick<Stream, 'stream'>[], sourceCounts: Record<string, number>): string[] {
  return streams
    .filter((s) => !((sourceCounts[s.stream] ?? 0) > 0))
    .map((s) => s.stream)
    .sort((a, b) => a.localeCompare(b))
}

/** Streams holding data on disk with no DDM row (exact name), largest first. */
export function streamsWithoutDdm(streams: Pick<Stream, 'stream' | 'size_on_disk'>[], ddm: Pick<DdmStream, 'stream'>[]): string[] {
  const modelled = new Set(ddm.map((r) => r.stream))
  return streams
    .filter((s) => s.size_on_disk > 0 && !modelled.has(s.stream))
    .sort((a, b) => b.size_on_disk - a.size_on_disk || a.stream.localeCompare(b.stream))
    .map((s) => s.stream)
}
